<template>
  <div>
    <b-row class="mb-4">
      <b-col>
        <h4 class="d-inline mr-3">
          {{ endpoint ? endpoint.name : 'Loading...' }}
          <span class="text-muted h5 ml-2">({{ endpoint ? endpoint.throttle_type : '' }})</span>
        </h4>
      </b-col>
    </b-row>
    <b-row class="m-auto mb-4 text-center" v-if="endpoint">
      <b-col xl="6" class="mb-2">
        <b-row>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">Potential</div>
              <span class="h5">
                {{ summary.qpsPotential }}
              </span> QPS
              <span class="text-muted">
                ({{ percentOf(summary.qpsPotential, summary.qpsPotential) }}%)
              </span>
            </b-card>
          </b-col>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">Smart Throttled</div>
              <span class="h5">
                <span class="text-danger font-weight-bold">-</span>
                {{ summary.qpsBlockedAuto }}</span> QPS
              <span class="text-muted">
                ({{ percentOf(summary.qpsBlockedAuto, summary.qpsPotential) }}%)
              </span>
            </b-card>
          </b-col>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">QPS Limited</div>
              <span class="h5">
                <span class="text-danger font-weight-bold">-</span>
                {{ summary.qpsBlockedQps }}
              </span> QPS
              <span class="text-muted">
               ({{ percentOf(summary.qpsBlockedQps, summary.qpsPotential) }}%)
              </span>
            </b-card>
          </b-col>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">Outbound</div>
              <span class="h5">
                <span class="text-primary font-weight-bold">=</span>
                {{ summary.qpsOut }}
              </span> QPS
              <span class="text-muted">
               ({{ percentOf(summary.qpsOut, summary.qpsPotential) }}%)
              </span>
            </b-card>
          </b-col>
        </b-row>
        </b-col>
      <b-col xl="6">
        <b-row>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">Limit</div>
              <span class="h5">
                {{ summary.qpsLimit }}
              </span> QPS
              <span class="text-muted">
               ({{ percentOf(summary.qpsOut, summary.qpsLimit) }}%)
              </span>
            </b-card>
          </b-col>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">Target Metric</div>
              <span class="h5">
                {{ format('target_metric', summary.targetMetric) }}
              </span>
            </b-card>
          </b-col>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">RPM</div>
              <span class="h5">
                {{ format('rpm', summary.rpm) }}
              </span>
            </b-card>
          </b-col>
          <b-col lg="3">
            <b-card class="bg-lighter">
              <div class="mb-1 text-muted">Net RPM</div>
              <span class="h5">
                {{ format('nrpm', summary.nrpm) }}
              </span>
              <span class="text-muted">
               ({{ percentOf(summary.nrpm, summary.rpm) }}%)
              </span>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>


    <table-card class="mt-5" v-if="endpoint"
      :items="stats"
      :fields="cols"
      title="Per Node Throttle Stats">
      <template v-for="stat in cols" :slot="'cell('+stat+')'" slot-scope="data" v-key="stat">
        {{ format(stat, data.value) }}
      </template>
    </table-card>

    <skeleton-rectangle v-else></skeleton-rectangle>
  </div>
</template>

<script>
import TableCard from "../../components/TableCard.vue";
import {DemandApi} from "../api";
import numeral from "numeral";
import TableSkeleton from "../../reports/components/TableSkeleton.vue";
import SkeletonRectangle from "../../components/SkeletonRectangle.vue";
export default {
  name: 'ThrottleStats',
  props: {
    cid: { required: true },
    epid: { required: true }
  },
  mounted () {
    DemandApi.throttleStats(this.epid).then(stats => {
      this.stats = stats && stats.snapshots ? stats.snapshots : []
    }).catch(e => {
      this.$notify.error("Error", "No throttle stats found or error retrieving")
    })

    DemandApi.endpoints(this.cid).then((l) => {
      for(let ep of l) {
        if (ep.id === this.epid) {
          this.endpoint = ep
          return
        }
      }
      this.$notify.error("Not Found", "Uneable to find given endpoint ID")
    })
  },
  data () {
    return {
      endpoint: null,
      cols: [ 'node_id', 'target_metric', 'qps_potential', 'local_qps_limit',
        'qps_blocked_auto', 'qps_blocked_qps', 'qps_out', 'segments', 'passing_segments', 'rpm', 'bid_rate', 'clear_rate', 'fill_rate' ],
      stats: []
    }
  },
  methods: {
    percentOf (sm, lg) {
      return this.formatFloat((sm / lg) * 100);
    },
    formatFloat (val) {
      return this.formatNum(val, '0[.][00]a')
    },
    formatNum (val, format) {
      return numeral(Number(val).toPrecision(3)).format(format)
    },
    format (stat, val) {
      switch (stat) {
        case 'target_metric':
          switch (this.endpoint.throttle_type) {
            case 'SMART_RPM':
            case 'FUZZY_RPM':
            case 'FUZZY_NRPM':
            case 'SMART_RPM_PRIORITY':
              return '$' + this.formatFloat(val)
            case 'SMART_FILLRATE':
            case 'SMART_FILLRATE_PRIORITY':
            case 'SMART_BIDRATE':
            case 'FUZZY_BIDRATE':
              return this.formatFloat(val) + '%'
            case 'SMART_BIDS':
            case 'FUZZY_BIDS':
              return val + ' bids'
            case 'PLACEMENT':
              return 'ANY BID'
            default:
              return val
          }
        case 'bid_rate':
        case 'fill_rate':
        case 'clear_rate':
          return this.formatFloat(val) + '%'
        case 'rpm':
        case 'nrpm':
          return '$' + this.formatFloat(val)
        case 'node_id':
          return val
        default:
          return this.formatNum(val, '0,0')
      }
    }
  },
  computed: {
    summary () {
      const sum = {
        qpsPotential: 0,
        qpsLimit: 0,
        qpsBlockedAuto: 0,
        qpsBlockedQps: 0,
        qpsOut: 0,
        targetMetric: 0,
        rpm: 0,
        nrpm: 0
      }

      let tm = 0, rpm = 0, nrpm = 0, totalQpsOut = 0
      for (const stat of this.stats) {
        sum.qpsPotential += stat.qps_potential
        sum.qpsLimit += stat.local_qps_limit
        sum.qpsBlockedAuto += stat.qps_blocked_auto
        sum.qpsBlockedQps += stat.qps_blocked_qps
        sum.qpsOut += stat.qps_out

        tm += stat.target_metric
        // weight these by qps share
        rpm += stat.rpm * stat.qps_out
        nrpm += stat.nrpm * stat.qps_out

        totalQpsOut += stat.qps_out
      }

      sum.targetMetric = (tm / this.stats.length)
      sum.rpm = totalQpsOut > 0 ? (rpm / totalQpsOut) : 0
      sum.nrpm = totalQpsOut > 0 ? (nrpm / totalQpsOut) : 0

      return sum
    }
  },
  components: {
    SkeletonRectangle,
    TableSkeleton,
    TableCard
  }
}
</script>



// WEBPACK FOOTER //
// src/demand/pages/ThrottleStats.vue